import React from "react";
import { Link } from "react-router-dom";
import { Crown, Lock, Eye, Star, ListPlus, Sparkles } from "lucide-react";
import { MarketBadge } from "@/components/badges/Badges";
import { useLaunch } from "@/context/LaunchContext";
import { localizeGrade } from "@/presentation/liveLabels";
import { MarketFixture, MarketRecommendation } from "@/types/marketHub";
import { useI18n } from "@/i18n";

type Lang = "tr" | "en";
const t = (lang: Lang, tr: string, en: string) => (lang === "tr" ? tr : en);
const fin = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function RiskPill({ risk, code, lang }: { risk?: string | null; code: string; lang: Lang }) {
  const label = risk ? localizeGrade(risk, lang) : null;
  if (!label) return null;
  return <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-foreground/70">{label} {t(lang, "Risk", "Risk")}</span>;
}

function RecRow({ rec, lang, emphasis }: { rec: MarketRecommendation; lang: Lang; emphasis?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 ${emphasis ? "border-atlas/25 bg-atlas/[0.06]" : "border-white/8 bg-white/[0.02]"}`} data-testid="market-rec-row">
      <div className="flex min-w-0 items-center gap-2">
        <MarketBadge code={rec.market_code} backendLabel={rec.market_name} size="sm" />
        <RiskPill risk={rec.risk} code={rec.market_code} lang={lang} />
      </div>
      {fin(rec.reference_odd) && <span className="num shrink-0 text-sm font-bold text-atlas-300">{rec.reference_odd!.toFixed(2)}</span>}
    </div>
  );
}

function LockedTeaser({ text, plan, lang }: { text: string; plan: string; lang: Lang }) {
  return (
    <Link to="/app/membership" data-testid="market-upsell" className="flex items-center justify-between gap-2 rounded-xl border border-gold/25 bg-gold/[0.06] px-3 py-2.5 transition hover:bg-gold/10">
      <span className="flex items-center gap-2 text-xs font-semibold text-foreground/80"><Lock className="h-3.5 w-3.5 text-gold-400" /> {text}</span>
      <span className="shrink-0 rounded-full border border-gold/30 bg-gold/10 px-2 py-0.5 text-[10px] font-bold text-gold-300">{plan} {t(lang, "ile aç", "unlock")}</span>
    </Link>
  );
}

// Additive, authoritative Market Hub product sections. Entitlement-gated so the
// plans feel genuinely different (primary = all; alternatives = PRO; watching =
// ELITE) with contextual value upsell. Never synthesizes selections.
export function MarketHubSections({ fixture, variant = "detail" }: { fixture: MarketFixture | null | undefined; variant?: "detail" | "card" }) {
  const { lang } = useI18n();
  const { hasPlan } = useLaunch();
  if (!fixture) return null;

  const primary = fixture.primary && fixture.primary.is_recommendation !== false ? fixture.primary : null;
  const alts = fixture.alternatives || [];
  const watching = fixture.watching || [];
  // Authoritative counts drive locked teasers even when the server redacts the
  // protected arrays (empty) for an unentitled plan.
  const altCount = fixture.alternative_count ?? alts.length ?? 0;
  const watchCount = fixture.watching_count ?? watching.length ?? 0;
  const maxAlts = variant === "card" ? 3 : alts.length;
  const maxWatch = variant === "card" ? 2 : watching.length;
  const canAlts = hasPlan("PRO");
  const canWatch = hasPlan("ELITE");

  const nothing = !primary && altCount === 0 && watchCount === 0;
  if (nothing) {
    if (variant === "card") return null;
    return (
      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-sm text-muted-foreground" data-testid="market-empty">
        {t(lang, "ATLAS maçları izlemeye devam ediyor. Yeni bir değerlendirme oluştuğunda burada görünecek.", "ATLAS keeps monitoring matches. New evaluations will appear here.")}
      </div>
    );
  }

  const wrap = variant === "card" ? "space-y-2.5" : "space-y-4";
  return (
    <div className={wrap} data-testid="market-hub-sections">
      {/* ANA SEÇİM */}
      {primary ? (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5 text-atlas-300"><Star className="h-3 w-3" /> {t(lang, "ATLAS Ana Seçimi", "ATLAS Primary Pick")}</p>
          <RecRow rec={primary} lang={lang} emphasis />
        </div>
      ) : (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] px-3 py-2.5 text-xs text-muted-foreground" data-testid="market-no-primary">
          {t(lang, "Şu anda yayın kriterini geçen seçim yok.", "No selection currently meets the publish criteria.")}
        </div>
      )}

      {/* ALTERNATİFLER */}
      {altCount > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5 text-muted-foreground"><ListPlus className="h-3 w-3" /> {t(lang, "Alternatif Seçimler", "Alternative Picks")}</p>
          {canAlts && alts.length > 0 ? (
            <div className="space-y-2">{alts.slice(0, maxAlts).map((r, i) => <RecRow key={`${r.market_code}-${i}`} rec={r} lang={lang} />)}</div>
          ) : (
            <LockedTeaser text={t(lang, `${altCount} alternatif ATLAS seçimi mevcut`, `${altCount} alternative ATLAS picks available`)} plan="PRO" lang={lang} />
          )}
        </div>
      )}

      {/* İZLİYOR — intentionally NOT a betting signal (no odds, muted chips) */}
      {watchCount > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5 text-muted-foreground"><Eye className="h-3 w-3" /> {t(lang, "ATLAS İzliyor", "ATLAS Watching")}</p>
          {canWatch && watching.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" data-testid="market-watching">
              {watching.slice(0, maxWatch).map((r, i) => (
                <span key={`${r.market_code}-${i}`} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.02] px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  <Eye className="h-3 w-3 opacity-60" /> <MarketBadge code={r.market_code} backendLabel={r.market_name} size="sm" />
                </span>
              ))}
            </div>
          ) : (
            <LockedTeaser text={t(lang, `ATLAS'ın izlediği ${watchCount} ek senaryo`, `${watchCount} extra scenarios ATLAS is watching`)} plan="ELITE" lang={lang} />
          )}
        </div>
      )}
    </div>
  );
}

// Locked preview for ELITE-only advanced analytics (xG / form / H2H / trends).
export function AdvancedLock({ plan = "ELITE", compact }: { plan?: string; compact?: boolean }) {
  const { lang } = useI18n();
  return (
    <Link to="/app/membership" data-testid="advanced-lock" className={`block rounded-xl border border-gold/25 bg-gold/[0.06] transition hover:bg-gold/10 ${compact ? "px-3 py-2.5" : "p-4"}`}>
      <p className="flex items-center gap-2 text-sm font-bold text-foreground"><Lock className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Gelişmiş Maç Analizi" : "Advanced Match Analysis"}</p>
      {!compact && <p className="mt-1 text-xs text-muted-foreground">{lang === "tr" ? "xG, form, H2H, trend ve detaylı metrikler" : "xG, form, H2H, trends and detailed metrics"}</p>}
      <span className="mt-2 inline-block rounded-full border border-gold/30 bg-gold/10 px-2.5 py-0.5 text-[10px] font-bold text-gold-300">{plan} {lang === "tr" ? "ile aç" : "unlock"}</span>
    </Link>
  );
}

// Compact single-line hint for collapsed cards.
export function MarketHubCardHint({ fixture }: { fixture: MarketFixture | null | undefined }) {
  const { lang } = useI18n();
  if (!fixture) return null;
  const altN = fixture.alternative_count ?? fixture.alternatives?.length ?? 0;
  const watchN = fixture.watching_count ?? fixture.watching?.length ?? 0;
  if (!fixture.primary && altN === 0 && watchN === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground" data-testid="market-card-hint">
      {fixture.primary && <span className="inline-flex items-center gap-1 text-atlas-300"><Crown className="h-3 w-3" /> {lang === "tr" ? "Ana Seçim" : "Primary"}</span>}
      {altN > 0 && <span>· {altN} {lang === "tr" ? "alternatif" : "alt"}</span>}
      {watchN > 0 && <span className="inline-flex items-center gap-1">· <Eye className="h-3 w-3" /> {watchN} {lang === "tr" ? "izleniyor" : "watching"}</span>}
    </div>
  );
}
